import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, X } from 'lucide-react';

import { WebIDData } from '../../services/webids';
import { checkOnboardingStatus } from '../../hooks/useOnboardingGuard';
import { Button } from '../ui';

/**
 * @component OnboardingNotification
 * @description Notificación para WebIDs que requieren completar onboarding
 */

interface OnboardingNotificationProps {
  webIds: WebIDData[];
  onDismiss?: () => void;
  className?: string;
}

const OnboardingNotification: React.FC<OnboardingNotificationProps> = ({
  webIds,
  onDismiss,
  className = ''
}) => {
  // Filtrar WebIDs que requieren onboarding
  const webIdsRequiringOnboarding = webIds.filter(webId => {
    const { required } = checkOnboardingStatus(webId);
    return required;
  });

  if (webIdsRequiringOnboarding.length === 0) {
    return null;
  }

  const isMultiple = webIdsRequiringOnboarding.length > 1;
  const firstWebId = webIdsRequiringOnboarding[0];

  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        
        <div className="flex-1">
          <h3 className="text-sm font-medium text-yellow-800 mb-1">
            {isMultiple 
              ? `${webIdsRequiringOnboarding.length} pulseras requieren configuración`
              : 'Pulsera requiere configuración'
            }
          </h3>
          
          <p className="text-sm text-yellow-700 mb-3">
            {isMultiple
              ? 'Completa la configuración de tus pulseras para activar todas las funciones de emergencia.'
              : `La pulsera ${firstWebId.id} necesita configuración inicial para funcionar correctamente.`
            }
          </p>

          <div className="flex items-center gap-3">
            <Link
              to={`/onboarding/${firstWebId.id}`}
              className="inline-flex items-center text-sm font-medium text-yellow-800 hover:text-yellow-900"
            >
              {isMultiple ? 'Configurar primera' : 'Configurar ahora'}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
            
            {isMultiple && (
              <Link
                to="/panel/webids"
                className="text-sm text-yellow-700 hover:text-yellow-800"
              >
                Ver todas ({webIdsRequiringOnboarding.length})
              </Link>
            )}
          </div>
        </div>

        {onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="text-yellow-600 hover:text-yellow-700 p-1"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default OnboardingNotification;
